import React, { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, Cell } from "recharts";
import { LineChart as LineIcon, ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { DarkPanel, Pill, IconBubble, AccentButton, SectionCard, HATCH } from "./StatusBadge";
import { engagement, fmtPct, fmtNum, fmtK, fmtShort, sumMetrics, inMonth, MONTHS, FORMATS } from "./contentUtils";

function Kpi({ label, value, accent, testid, children }) {
  return (
    <div className={`flex min-h-[190px] flex-col rounded-[28px] p-6 ${accent ? "bg-bordeaux text-white" : "text-white"}`} style={accent ? undefined : { backgroundColor: "#2D181E" }}>
      <div className="flex items-start justify-between"><p className="text-sm">{label}</p><span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20"><ArrowUpRight size={14} /></span></div>
      <p data-testid={testid} className="mt-auto font-sans text-5xl font-medium tracking-tight">{value}</p>
      {children}
    </div>
  );
}

function Ranking({ title, list, onOpen, testid }) {
  return (
    <SectionCard data-testid={testid}>
      <p className="eyebrow">{title}</p>
      <ol className="mt-3 space-y-1.5">
        {list.length === 0 && <p className="text-sm italic text-ink-muted">Pas encore assez de données.</p>}
        {list.map((i, n) => (
          <li key={i.id}><button onClick={() => onOpen(i.id)} className="flex w-full items-center gap-3 rounded-2xl px-2 py-2 text-left transition-colors hover:bg-cream">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#2D181E] text-xs font-medium text-white">{n + 1}</span>
            <span className="flex-1 truncate text-sm text-ink">{i.title || "Sans titre"}</span>
            <span className="text-xs text-ink-muted">{fmtK(i.metrics.views)} vues</span>
            <span className="w-16 text-right text-sm font-medium text-bordeaux">{fmtPct(i.eng)}</span>
          </button></li>
        ))}
      </ol>
    </SectionCard>
  );
}

function Hero({ data, avg, onTab }) {
  const ready = data.length >= 3;
  const ghost = [40, 70, 30, 90, 60, 35, 75, 50];
  return (
    <DarkPanel data-testid="stats-hero">
      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <div className="space-y-4">
          <Pill dark>Ce mois-ci</Pill>
          <IconBubble icon={LineIcon} dark />
          {ready ? <>
            <h3 className="font-sans text-3xl font-medium">Ton mois en un coup d'œil</h3>
            <p className="text-sm text-white/60">{data.length} posts trackés · {fmtNum(Math.round(avg))} vues en moyenne par post</p>
          </> : <>
            <h3 className="font-sans text-3xl font-medium">Tes statistiques arrivent</h3>
            <p className="text-sm text-white/60">Elles apparaissent dès tes 3 premiers posts trackés.</p>
          </>}
          <AccentButton testid="stats-go-tracking" icon={ArrowRight} onClick={() => onTab("tracking")}>Aller au Tracking</AccentButton>
        </div>
        <div className="h-56 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          {ready ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 16, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.45)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.45)" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }} contentStyle={{ borderRadius: 12, border: "none", fontSize: 12, color: "#2D181E" }} />
                <Bar dataKey="vues" radius={[10, 10, 10, 10]} barSize={26}>{data.map((d, n) => <Cell key={n} fill={d.vues >= avg ? "#7B113A" : "rgba(255,255,255,0.14)"} />)}</Bar>
                <ReferenceLine y={avg} stroke="#F3D2D9" strokeDasharray="4 4" label={{ value: "ta moyenne", fontSize: 10, fill: "#F3D2D9", position: "insideTopRight" }} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="relative flex h-full items-end justify-around">
              <div className="absolute inset-x-2 top-1/3 border-t border-dashed border-[#F3D2D9]/60"><span className="absolute -top-5 right-0 rounded-full bg-white/5 px-2 text-[10px] text-white/60">Ta moyenne apparaîtra ici</span></div>
              {ghost.map((h, n) => <div key={n} className="w-8 rounded-full border border-white/10 bg-white/[0.04]" style={{ height: `${h}%` }} />)}
            </div>
          )}
        </div>
      </div>
    </DarkPanel>
  );
}

export default function ContentStats({ items, onOpen, onTab }) {
  const [cursor, setCursor] = useState(new Date());
  const [fmt, setFmt] = useState("");
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const month = items.filter((i) => inMonth(i.date, y, m) && (!fmt || i.format === fmt));
  const pub = month.filter((i) => i.status === "publie").sort((a, b) => a.date.localeCompare(b.date));
  const measured = pub.filter((i) => i.metrics.views > 0).map((i) => ({ ...i, eng: engagement(i.metrics) }));
  const t = sumMetrics(measured);
  const avg = measured.length ? t.views / measured.length : 0;
  const ranked = [...measured].sort((a, b) => b.eng - a.eng);
  const formats = FORMATS;
  const byFormat = Object.entries(measured.reduce((a, i) => { const f = i.format || "Libre"; (a[f] = a[f] || []).push(i); return a; }, {}))
    .map(([f, arr]) => ({ f, v: engagement(sumMetrics(arr)) || 0, n: arr.length })).sort((a, b) => b.v - a.v);
  const data = measured.map((i) => ({ name: fmtShort(i.date), vues: i.metrics.views }));
  const dots = Math.max(month.length, pub.length);

  return (
    <div className="space-y-6">
      <Hero data={data} avg={avg} onTab={onTab} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-full bg-[#2D181E] p-1 text-white">
          <button data-testid="stats-prev" onClick={() => setCursor(new Date(y, m - 1, 1))} className="rounded-full p-1.5 hover:bg-white/10"><ChevronLeft size={14} /></button>
          <span data-testid="stats-month" className="min-w-[110px] text-center text-sm font-medium capitalize">{MONTHS[m]} {y}</span>
          <button data-testid="stats-next" onClick={() => setCursor(new Date(y, m + 1, 1))} className="rounded-full p-1.5 hover:bg-white/10"><ChevronRight size={14} /></button>
        </div>
        <div className="flex flex-wrap gap-2">
          {[["", "Tous les formats"], ...formats.map((f) => [f, f])].map(([k, l]) => <button key={l} data-testid={`stats-format-${k || "all"}`} onClick={() => setFmt(k)} className={`rounded-full px-4 py-1.5 text-sm transition-colors ${fmt === k ? "bg-bordeaux text-white" : "bg-white text-ink-soft hover:text-bordeaux"}`}>{l}</button>)}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi accent testid="stats-kpi-eng" label="Engagement moyen" value={fmtPct(engagement(t))} />
        <Kpi testid="stats-kpi-views" label="Vues (reach)" value={fmtK(t.views)} />
        <Kpi testid="stats-kpi-followers" label="Abonnés gagnés" value={`+${fmtNum(t.followers)}`} />
        <Kpi testid="stats-kpi-published" label="Posts publiés" value={<>{pub.length}<span className="text-xl text-white/50">/{month.length}</span></>}>
          <div className="mt-3 flex flex-wrap gap-1">{Array.from({ length: Math.min(dots, 30) }, (_, n) => <span key={n} className={`flex h-5 w-5 items-center justify-center rounded-full ${n < pub.length ? "bg-bordeaux" : "border border-white/20"}`}>{n < pub.length && <Check size={11} strokeWidth={3} />}</span>)}</div>
        </Kpi>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Ranking testid="stats-top" title="Top 5 · engagement" list={ranked.slice(0, 5)} onOpen={onOpen} />
        <Ranking testid="stats-flop" title="Flop 5 · engagement" list={ranked.length > 5 ? ranked.slice(-5).reverse() : []} onOpen={onOpen} />
        <SectionCard data-testid="stats-formats">
          <p className="eyebrow">Par format</p>
          <div className="mt-4 space-y-4">
            {byFormat.length === 0 && <p className="text-sm italic text-ink-muted">Pas encore de données.</p>}
            {byFormat.map((f) => (
              <div key={f.f}>
                <div className="flex justify-between text-sm"><span className="text-ink">{f.f} <span className="text-xs text-ink-muted">({f.n})</span></span><span className="font-medium text-bordeaux">{fmtPct(f.v)}</span></div>
                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-cream" style={HATCH}><div className="h-full rounded-full bg-bordeaux" style={{ width: `${Math.min(100, (f.v / (byFormat[0].v || 1)) * 100)}%` }} /></div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
